/**
 * O escape hatch — um componente Angular do projeto, dentro da tela.
 *
 * Nenhum catálogo cobre tudo, e quando o caso é específico demais para ser
 * declarado, a resposta não pode ser "não dá". Este tipo é a saída, e ele aceita
 * **uma** forma: `componentKey`, o nome de um componente **já compilado no
 * projeto**, escolhido no registro de componentes permitidos (`REGISTRY`).
 *
 * ---
 *
 * **A outra forma NÃO está implementada, e isso é decisão de segurança.**
 *
 * `props.source` guardaria TypeScript, HTML e CSS anexados à tela e compilados
 * em tempo de execução. Executar código vindo do banco é exatamente o problema
 * que a seção 8.3 do plano removeu ao deletar o `new Function` — a contagem em
 * produção deu zero e ele foi apagado em vez de isolado. Reabrir a mesma porta
 * pelo outro lado seria trocar de nome, não de risco.
 *
 * Com público duplo, o risco é concreto: um cliente final poderia gravar código
 * que roda no navegador de um operador interno, com o token dele.
 *
 * Se virar requisito real, o caminho é compilação no SERVIDOR, versionada e
 * restrita a papéis internos — nunca compilação no navegador do usuário. Até
 * lá, uma tela que declare `source` mostra o aviso em vez de executar.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';

import { REGISTRY } from '../../../interfaces/registry-services';
import { ScreenNode } from '../../model/node-props';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { TranslateTAPipe } from '../translate-ta.pipe';

@Component({
  selector: 'tem-custom-component',
  standalone: true,
  imports: [CommonModule, TranslateTAPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (hasAttachedSource()) {
      <div class="tem-custom__blocked">
        {{ 'builderScreen.customComponent.sourceBlocked' | translateTA }}
      </div>
    } @else if (component(); as tipo) {
      <ng-container *ngComponentOutlet="tipo; inputs: componentInputs()" />
    } @else if (componentKey()) {
      <!-- Nome escolhido que o registro não conhece. Dizer QUAL nome falhou é o
           que separa "não funciona" de "o nome está errado" — e o registro é
           preenchido em código, então o usuário não tem como conferir sozinho. -->
      <div class="tem-custom__missing">
        {{ 'builderScreen.customComponent.notRegistered' | translateTA }}
        <code>{{ componentKey() }}</code>
      </div>
    } @else {
      <div class="tem-custom__empty">
        {{ 'builderScreen.customComponent.none' | translateTA }}
      </div>
    }
  `,
  styles: [`
    :host { display: block; }

    .tem-custom__blocked,
    .tem-custom__missing,
    .tem-custom__empty {
      padding: var(--tem-space-2);
      border: 1px dashed var(--tem-border);
      border-radius: var(--tem-radius);
      color: var(--tem-muted);
      font-size: var(--tem-fs-sm);
    }

    .tem-custom__blocked {
      border-color: var(--tem-danger);
      color: var(--tem-danger);
    }

    code {
      font-family: var(--tem-font-mono, monospace);
      color: var(--tem-ink);
    }
  `]
})
export class CustomComponentRenderer implements NodeRenderer<'customComponent'> {

  readonly node = input.required<ScreenNode<'customComponent'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  readonly componentKey = computed<string>(() => this.node().props.componentKey ?? '');

  /**
   * A tela declara fonte anexado?
   *
   * Verificado ANTES de qualquer outra coisa: uma tela que pede compilação em
   * tempo de execução não deve nem tentar montar o componente por chave, para
   * não dar a impressão de que funcionou pela metade.
   */
  readonly hasAttachedSource = computed<boolean>(() => {
    const source = this.node().props.source;

    if (source === undefined) {
      return false;
    }

    return Boolean(source.typescript ?? source.html ?? source.css);
  });

  /** O componente registrado, ou `null` quando o nome não existe no registro. */
  readonly component = computed<unknown>(() => {
    const key = this.componentKey();

    if (key.length === 0) {
      return null;
    }

    return REGISTRY.get(key) ?? null;
  });

  readonly componentInputs = computed<Record<string, unknown>>(
    () => ({ ...(this.node().props.inputs ?? {}) })
  );
}
